import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logoImg from "../../assets/images/logo1.png";

const sections = [
  { id: "definitions", title: "Definitions and Key Terms" },
  { id: "introduction", title: "Introduction and Scope" },
  { id: "personal-data", title: "Personal Data Collection" },
  { id: "processing", title: "Data Processing and Usage" },
  { id: "storage", title: "Data Storage and Security" },
  { id: "analytics", title: "Analytics, Advertising, and Third-Party Services" },
  { id: "rights", title: "Your Rights and Choices" },
  { id: "retention", title: "Data Retention and Deletion" },
  { id: "transfers", title: "International Data Transfers and Legal Jurisdiction" },
  { id: "children", title: "Children Privacy" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "legal", title: "Legal Information and Contact Details" },
];

const List = ({ items }) => (
  <ul className="mb-2 list-disc space-y-1 pl-6">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const Section = ({ id, number, title, children }) => (
  <section id={id} className="mb-8 scroll-mt-6">
    <h3 className="mb-2 text-xl font-bold">
      {number}. {title.toUpperCase()}
    </h3>
    {children}
  </section>
);

const Subheading = ({ children }) => (
  <h4 className="mb-1 mt-4 font-semibold">{children}</h4>
);

const Label = ({ children }) => (
  <span className="mb-1 block font-semibold">{children}</span>
);

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <header className="w-full border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center px-4 py-6">
          <Link to="/" aria-label="MoveRelief home">
            <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
          </Link>
        </div>
      </header>

      <div className="w-full bg-[#7C3A8D]">
        <div className="mx-auto max-w-5xl px-4 py-8">
          <h1 className="text-3xl font-bold text-white md:text-4xl">Privacy Policy</h1>
        </div>
      </div>

      <main className="w-full">
        <div className="mx-auto max-w-3xl px-4 py-10 text-left text-base leading-relaxed text-gray-800">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-6 text-sm font-medium text-gray-600 hover:text-[#7C3A8D]"
          >
            ← Back
          </button>

          <h2 className="mb-4 text-2xl font-bold">TABLE OF CONTENTS</h2>
          <ol className="mb-8 list-decimal space-y-1 pl-6">
            {sections.map((section) => (
              <li key={section.id}>
                <a className="text-[#7C3A8D] underline hover:text-[#5F2D6F]" href={`#${section.id}`}>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>

          <Section id="definitions" number="1" title="Definitions and Key Terms">
            <Subheading>1.1 Momiji Group Inc. and Service Terms</Subheading>
            <List
              items={[
                'MoveRelief ("we," "us," or "our") means the MoveRelief.pro service operated by Momiji Group Inc.',
                "Service means all features, programs, content, plans, posture scan flows, checkout pages, and customer support tools provided through MoveRelief.",
                "Platform means our website, web app, and related services available through moverelief.pro on any device.",
                'User means any individual accessing or using our services ("you" or "your").',
              ]}
            />

            <Subheading>1.2 Data and Privacy Terms</Subheading>
            <List
              items={[
                "Personal Data means information relating to an identified or identifiable person.",
                "Processing means any operation performed on personal data, including collection, storage, use, disclosure, or deletion.",
                "Data Controller means MoveRelief, where we determine the purposes and means of processing personal data.",
                "Data Processor means a third party that processes personal data on our behalf.",
                "Cookie means a small text file stored on your device that helps remember preferences or measure platform usage.",
              ]}
            />

            <Subheading>1.3 Security Terms</Subheading>
            <List
              items={[
                "Authentication means the process of verifying user identity.",
                "Encryption means encoding information to reduce the risk of unauthorized access.",
                "Token means a unique identifier used for secure authentication or payment processing.",
                "SSL/TLS means security protocols for encrypted data transmission.",
              ]}
            />
          </Section>

          <Section id="introduction" number="2" title="Introduction and Scope">
            <Subheading>2.1 Policy Overview</Subheading>
            <p className="mb-2">
              This Privacy Policy explains how MoveRelief collects, uses, stores, and protects personal data when you use MoveRelief.pro. It also explains the choices and rights available to you.
            </p>

            <Subheading>2.2 Policy Application</Subheading>
            <List
              items={[
                "All users of MoveRelief globally.",
                "All data collection methods used on MoveRelief.pro.",
                "All service features, payment flows, posture scan flows, and account functionality.",
                "All platform versions, updates, and related support channels.",
              ]}
            />

            <Subheading>2.3 Policy Updates</Subheading>
            <List
              items={[
                "We may update this Privacy Policy from time to time.",
                "Material changes may be communicated by email or through the platform.",
                "Continued use of MoveRelief after changes become effective means you accept the updated policy.",
              ]}
            />
          </Section>

          <Section id="personal-data" number="3" title="Personal Data Collection">
            <Subheading>3.1 Account Information</Subheading>
            <Label>A. Essential Data</Label>
            <List
              items={[
                "Email address for authentication, account access, receipts, and support.",
                "Name, billing details, and limited payment metadata collected during payment processing.",
                "Last sign-in timestamp, unique account identifiers, and IP addresses.",
              ]}
            />
            <Label>B. Optional Data</Label>
            <List
              items={[
                "Phone number if provided through a payment processor or support interaction.",
                "User preferences, communication choices, and settings you choose to share.",
              ]}
            />

            <Subheading>3.2 Service Usage Data</Subheading>
            <Label>A. Back Relief Plan and Posture Scan Data</Label>
            <List
              items={[
                "Answers you provide about back discomfort, mobility, habits, and goals.",
                "Optional posture scan inputs or images if you choose to use scan-based features.",
                "Generated plan outputs, completion timestamps, and service performance metrics.",
              ]}
            />
            <span className="mb-2 block text-xs text-gray-500">
              Note: Some questionnaire answers or scan inputs may be processed in real time to generate your plan and may not be stored unless needed to provide the service, troubleshoot issues, or comply with legal obligations.
            </span>
            <Label>B. Interaction Data</Label>
            <List
              items={[
                "Features accessed and time spent on the platform.",
                "Navigation patterns, page views, device information, and referral data.",
              ]}
            />

            <Subheading>3.3 Payment Information and Processing</Subheading>
            <p className="mb-2">Payments are processed by third-party payment providers. We do not store complete card numbers.</p>
            <List
              items={[
                "Tokenized payment method identifiers.",
                "Last four digits of payment cards and expiration dates where made available by the processor.",
                "Transaction status, subscription status, billing events, and refund records.",
              ]}
            />

            <Subheading>3.4 Technical and Device Data</Subheading>
            <Label>A. Device Information</Label>
            <List items={["Operating system and version.", "Browser type and version.", "Screen resolution, device type, language preferences, and time zone."]} />
            <Label>B. Connection Data</Label>
            <List items={["IP address.", "Network information and connection type.", "Approximate geographic location derived from IP address."]} />
            <Label>C. Performance Data</Label>
            <List items={["Load times.", "Error messages.", "System performance metrics, network latency, and application response times."]} />
          </Section>

          <Section id="processing" number="4" title="Data Processing and Usage">
            <Subheading>4.1 Primary Processing Purposes</Subheading>
            <Label>A. Service Provision</Label>
            <List items={["Account creation and management.", "Authentication and security.", "Personalized back relief plan generation.", "Feature access, customer support, and service optimization."]} />
            <Label>B. Payment Processing</Label>
            <List items={["Subscription management.", "Payment authorization.", "Fraud prevention.", "Transaction records and billing support."]} />
            <Label>C. Communication</Label>
            <List items={["Service updates and notifications.", "Security alerts.", "Product information where permitted.", "Support responses and legal notices."]} />

            <Subheading>4.2 Secondary Processing Purposes</Subheading>
            <List
              items={[
                "Usage pattern analysis and product improvement.",
                "Feature optimization, bug detection, and performance monitoring.",
                "Aggregate analytics, trend analysis, benchmarking, and service reliability improvements.",
              ]}
            />

            <Subheading>4.3 Legal Bases for Processing</Subheading>
            <List
              items={[
                "Contractual necessity, including account management, service provision, payment processing, feature access, and support.",
                "Legal obligations, including tax, financial, regulatory, and safety requirements.",
                "Legitimate interests, including service improvement, fraud prevention, security, and technical optimization.",
                "Consent-based processing, including marketing communications, optional features, analytics participation, and certain third-party integrations.",
              ]}
            />
          </Section>

          <Section id="storage" number="5" title="Data Storage and Security">
            <Subheading>5.1 Storage Location and Data Transfers</Subheading>
            <List
              items={[
                "Personal data may be stored using secure cloud infrastructure and service providers.",
                "Data may be transmitted globally using encrypted channels.",
                "We use appropriate safeguards for international data transfers where required.",
                "We maintain security measures designed to protect the confidentiality, integrity, and availability of personal data.",
              ]}
            />

            <Subheading>5.2 Security Measures</Subheading>
            <Label>A. Authentication and Access</Label>
            <List items={["Passwordless authentication or verification flows where available.", "Session management.", "Role-based access control and least-privilege access.", "Access logging, monitoring, and periodic review."]} />
            <Label>B. Data Protection</Label>
            <List items={["Encryption for data in transit.", "Secure data storage practices.", "Regular security reviews and vendor assessments.", "Payment processing handled by PCI-compliant providers."]} />
            <Label>C. Monitoring and Maintenance</Label>
            <List items={["System monitoring and security event logging.", "Regular updates and patch management.", "Incident response procedures and change management practices."]} />

            <Subheading>5.3 Data Breach Notification Procedures</Subheading>
            <p className="mb-2">
              If we become aware of a personal data breach that requires notification, we will assess the incident and notify affected users or regulators where legally required.
            </p>
          </Section>

          <Section id="analytics" number="6" title="Analytics, Advertising, and Third-Party Services">
            <Subheading>6.1 Analytics and Advertising</Subheading>
            <p className="mb-2">
              We may use analytics and advertising tools to understand platform usage, measure campaigns, improve user experience, and show relevant offers.
            </p>
            <List
              items={[
                "Analytics providers may collect usage, device, and interaction data.",
                "Advertising partners may use cookies, pixels, or similar technologies.",
                "You can manage cookie and tracking preferences through browser settings or platform controls where available.",
              ]}
            />

            <Subheading>6.2 Service Providers</Subheading>
            <List
              items={[
                "Payment processors for checkout, subscription, and refund handling.",
                "Hosting and infrastructure providers.",
                "Email, support, analytics, fraud-prevention, and customer communication tools.",
                "AI, image, or posture analysis providers where needed to deliver optional scan-based features.",
              ]}
            />
          </Section>

          <Section id="rights" number="7" title="Your Rights and Choices">
            <Subheading>7.1 Privacy Rights</Subheading>
            <p className="mb-2">Depending on your location, you may have rights to:</p>
            <List
              items={[
                "Access the personal data we hold about you.",
                "Request correction of inaccurate personal data.",
                "Request deletion of personal data.",
                "Object to or restrict certain processing.",
                "Request a copy of your data in a portable format.",
                "Withdraw consent where processing is based on consent.",
              ]}
            />

            <Subheading>7.2 How to Exercise Rights</Subheading>
            <p className="mb-2">
              You may contact us at <a className="text-[#7C3A8D] underline" href="mailto:info@moverelief.pro">info@moverelief.pro</a>. We may need to verify your identity before completing a request.
            </p>

            <Subheading>7.3 Marketing Choices</Subheading>
            <List
              items={[
                "You can unsubscribe from marketing emails using the unsubscribe link in those emails.",
                "You may still receive transactional or service-related messages.",
                "You can adjust browser settings to limit cookies and similar technologies.",
              ]}
            />
          </Section>

          <Section id="retention" number="8" title="Data Retention and Deletion">
            <Subheading>8.1 Retention Periods</Subheading>
            <List
              items={[
                "Account data is retained while your account is active and for a reasonable period afterward.",
                "Payment and transaction records are retained as required for accounting, tax, fraud prevention, and legal purposes.",
                "Support communications may be retained to resolve issues and improve customer service.",
                "Analytics data may be aggregated or anonymized and retained for product improvement.",
              ]}
            />

            <Subheading>8.2 Deletion Requests</Subheading>
            <p className="mb-2">
              You may request deletion by contacting us. Some information may be retained where necessary for legal compliance, dispute resolution, fraud prevention, or legitimate business records.
            </p>
          </Section>

          <Section id="transfers" number="9" title="International Data Transfers and Legal Jurisdiction">
            <Subheading>9.1 International Transfers</Subheading>
            <p className="mb-2">
              MoveRelief is available globally. Your data may be processed in countries other than your country of residence. Where required, we use appropriate safeguards for these transfers.
            </p>

            <Subheading>9.2 Legal Jurisdiction</Subheading>
            <p className="mb-2">
              Momiji Group Inc. is located in the United States. This Privacy Policy is intended to comply with applicable privacy laws where our services are offered.
            </p>
          </Section>

          <Section id="children" number="10" title="Children Privacy">
            <p className="mb-2">
              MoveRelief is not intended for children under 18. We do not knowingly collect personal data from children. If you believe a child has provided personal data, contact us so we can review and delete it where appropriate.
            </p>
          </Section>

          <Section id="changes" number="11" title="Changes to This Policy">
            <List
              items={[
                "We may revise this Privacy Policy as our services, legal obligations, or processing practices change.",
                "The updated version will be posted on this page.",
                "If changes are material, we may provide additional notice.",
                "Continued use of MoveRelief after changes become effective indicates acceptance of the revised policy.",
              ]}
            />
          </Section>

          <Section id="legal" number="12" title="Legal Information and Contact Details">
            <Subheading>12.1 Momiji Group Inc. Information</Subheading>
            <p className="mb-2">For all inquiries, including privacy-related matters:</p>
            <List
              items={[
                "Email: info@moverelief.pro",
                "Website: https://moverelief.pro",
                "Postal Address: 2604 Whittier Place, Wilmington, DE, 19808, United States",
              ]}
            />
            <span className="block text-xs text-gray-500">
              We will handle privacy inquiries according to applicable legal response timelines.
            </span>
          </Section>
        </div>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-5xl px-4 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-md">
              <Link to="/" className="mb-4 inline-block" aria-label="MoveRelief home">
                <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
              </Link>
              <p className="text-sm leading-relaxed text-gray-400">
                MoveRelief.pro provides personalized back relief plans, posture scan flows, and gentle movement guidance designed for everyday comfort.
              </p>
            </div>

            <div>
              <h3 className="mb-3 font-semibold">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/terms">Terms of Service</Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/cookie">Cookie Policy</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-800 pt-6 text-sm text-gray-400">
            © 2026 Momiji Group Inc. All Rights Reserved.
            <br />
            2604 Whittier Place, Wilmington, DE, 19808
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
